import React, { useMemo, useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";
import {
  Ban,
  ChevronRight,
  Code2,
  Loader2,
  Terminal,
  Check,
} from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { FilePreview } from "@/components/ui/file-preview";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";

const chatBubbleVariants = cva(
  "group/message relative rounded-md py-1 px-3 max-w-[85%] text-sm inline-block",
  {
    variants: {
      isUser: {
        true: "bg-gray-100 text-gray-900",
        false: "bg-white text-gray-900",
      },
      animation: {
        none: "",
        slide: "duration-300 animate-in fade-in-0",
        scale: "duration-300 animate-in fade-in-0 zoom-in-75",
        fade: "duration-500 animate-in fade-in-0",
      },
    },
    compoundVariants: [
      {
        isUser: true,
        animation: "slide",
        class: "slide-in-from-right",
      },
      {
        isUser: false,
        animation: "slide",
        class: "slide-in-from-left",
      },
      {
        isUser: true,
        animation: "scale",
        class: "origin-bottom-right",
      },
      {
        isUser: false,
        animation: "scale",
        class: "origin-bottom-left",
      },
    ],
  }
);

type Animation = VariantProps<typeof chatBubbleVariants>["animation"];

interface Attachment {
  name?: string;
  contentType?: string;
  url: string;
}

interface PartialToolCall {
  state: "partial-call";
  toolName: string;
}

interface ToolCall {
  state: "call";
  toolName: string;
}

interface ToolResult {
  state: "result";
  toolName: string;
  result: {
    __cancelled?: boolean;
    [key: string]: any;
  };
}

type ToolInvocation = PartialToolCall | ToolCall | ToolResult;

interface ReasoningPart {
  type: "reasoning";
  reasoning: string;
}

interface ToolInvocationPart {
  type: "tool-invocation";
  toolInvocation: ToolInvocation;
}

interface TextPart {
  type: "text";
  text: string;
}

// For compatibility with AI SDK types, not used
interface SourcePart {
  type: "source";
}

type MessagePart = TextPart | ReasoningPart | ToolInvocationPart | SourcePart;

export interface Message {
  id: string;
  backendId?: string; // server-provided UUID for feedback
  role: "user" | "assistant" | (string & {});
  content: string;
  createdAt?: Date;
  experimental_attachments?: Attachment[];
  toolInvocations?: ToolInvocation[];
  parts?: MessagePart[];
}

export interface ChatMessageProps extends Message {
  showTimeStamp?: boolean;
  animation?: Animation;
  actions?: React.ReactNode;
}

// Extract response content from JSON-like message content
const extractResponseContent = (content: string): string => {
  try {
    const parsed = JSON.parse(content);
    if (parsed.response) {
      return parsed.response;
    }
  } catch {
    const responseMatch = content.match(/'response':\s*"([^"]+)"/);
    if (responseMatch) {
      return responseMatch[1].replace(/\\n/g, "\n").trim();
    }
  }
  return content;
};

export const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  createdAt,
  showTimeStamp = false,
  animation = "scale",
  actions,
  experimental_attachments,
  toolInvocations,
  parts,
}) => {
  // Clean content for assistant messages
  const displayContent =
    role === "assistant" ? extractResponseContent(content) : content;
  const files = useMemo(() => {
    return experimental_attachments?.map((attachment) => {
      const dataArray = dataUrlToUint8Array(attachment.url);
      const file = new File([dataArray], attachment.name ?? "Unknown");
      return file;
    });
  }, [experimental_attachments]);

  const isUser = role === "user";

  const formattedTime = createdAt?.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isUser) {
    return (
      <div
        className={cn(
          "flex flex-col mb-4",
          isUser ? "items-end" : "items-start"
        )}
      >
        {files ? (
          <div className="mb-1 flex flex-wrap gap-2">
            {files.map((file, index) => {
              return <FilePreview file={file} key={index} />;
            })}
          </div>
        ) : null}

        <div className={cn(chatBubbleVariants({ isUser, animation }))}>
          <MarkdownRenderer>{displayContent}</MarkdownRenderer>
        </div>

        {showTimeStamp && createdAt ? (
          <time
            dateTime={createdAt.toISOString()}
            className={cn(
              "mt-1 block px-1 text-xs opacity-50",
              animation !== "none" && "duration-500 animate-in fade-in-0"
            )}
          >
            {formattedTime}
          </time>
        ) : null}
      </div>
    );
  }

  if (parts && parts.length > 0) {
    return parts.map((part, index) => {
      if (part.type === "text") {
        return (
          <div
            className={cn(
              "flex flex-col mb-4",
              isUser ? "items-end" : "items-start"
            )}
            key={`text-${index}`}
          >
            <div className={cn(chatBubbleVariants({ isUser, animation }))}>
              <MarkdownRenderer>
                {role === "assistant"
                  ? extractResponseContent(part.text)
                  : part.text}
              </MarkdownRenderer>
              {actions ? (
                <div className="absolute -bottom-4 right-2 flex space-x-1 rounded-lg border bg-background p-1 text-foreground">
                  {actions}
                </div>
              ) : null}
            </div>

            {showTimeStamp && createdAt ? (
              <time
                dateTime={createdAt.toISOString()}
                className={cn(
                  "mt-1 block px-1 text-xs opacity-50",
                  animation !== "none" && "duration-500 animate-in fade-in-0"
                )}
              >
                {formattedTime}
              </time>
            ) : null}
          </div>
        );
      } else if (part.type === "reasoning") {
        return <ReasoningBlock key={`reasoning-${index}`} part={part} />;
      } else if (part.type === "tool-invocation") {
        return (
          <ToolCall
            key={`tool-${index}`}
            toolInvocations={[part.toolInvocation]}
          />
        );
      }
      return null;
    });
  }

  if (toolInvocations && toolInvocations.length > 0) {
    return <ToolCall toolInvocations={toolInvocations} />;
  }

  return (
    <div
      className={cn("flex flex-col mb-4", isUser ? "items-end" : "items-start")}
    >
      <div className={cn(chatBubbleVariants({ isUser, animation }))}>
        <MarkdownRenderer>{displayContent}</MarkdownRenderer>
        {actions ? (
          <div className="absolute -bottom-4 right-2 flex space-x-1 rounded-lg border bg-background p-1 text-foreground">
            {actions}
          </div>
        ) : null}
      </div>

      {showTimeStamp && createdAt ? (
        <time
          dateTime={createdAt.toISOString()}
          className={cn(
            "mt-1 block px-1 text-xs opacity-50",
            animation !== "none" && "duration-500 animate-in fade-in-0"
          )}
        >
          {formattedTime}
        </time>
      ) : null}
    </div>
  );
};

function dataUrlToUint8Array(data: string) {
  try {
    const base64 = data.split(",")[1] || "";
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch {
    return new Uint8Array();
  }
}

const ReasoningBlock = ({ part }: { part: ReasoningPart }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-2 flex flex-col items-start sm:max-w-[70%]">
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="group w-full overflow-hidden rounded-lg border bg-muted/50"
      >
        <div className="flex items-center p-2">
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
              <span>Thinking</span>
            </button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent forceMount>
          <motion.div
            initial={false}
            animate={isOpen ? "open" : "closed"}
            variants={{
              open: { height: "auto", opacity: 1 },
              closed: { height: 0, opacity: 0 },
            }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="border-t"
          >
            <div className="p-2">
              <div className="whitespace-pre-wrap text-xs">
                {part.reasoning}
              </div>
            </div>
          </motion.div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

function ToolCall({
  toolInvocations,
}: Pick<ChatMessageProps, "toolInvocations">) {
  if (!toolInvocations?.length) return null;

  return (
    <div className="space-y-2">
      {toolInvocations.map((invocation, index) => {
        const isCancelled =
          invocation.state === "result" &&
          invocation.result.__cancelled === true;

        // Get tool icon (simplified version from your ToolCallDisplay)
        const getToolIcon = (toolName: string) => {
          const iconMap: Record<
            string,
            React.ComponentType<{ className?: string }>
          > = {
            get_project_directory: Terminal,
            save_code: Code2,
            get_deployment_history: Terminal,
            get_user_projects: Terminal,
          };
          return iconMap[toolName] || Terminal;
        };

        // Get display name (simplified version from your ToolCallDisplay)
        const getToolDisplayName = (toolName: string): string => {
          const nameMap: Record<string, string> = {
            get_project_directory: "Setting up project directory",
            save_code: "Saving Terraform code",
            read_existing_code: "Reading existing code",
            compare_code: "Comparing code",
            compare_configurations: "Comparing configurations",
            format_terraform: "Formatting Terraform files",
            get_deployment_history: "Checking deployment history",
            get_user_projects: "Checking existing projects",
            get_project_resources: "Fetching project resources",
          };
          if (nameMap[toolName]) return nameMap[toolName];
          // Fallback: convert snake_case to Title Case
          return toolName
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
        };

        const IconComponent = getToolIcon(invocation.toolName);
        const displayName = getToolDisplayName(invocation.toolName);

        if (isCancelled) {
          return (
            <div
              key={index}
              className="border rounded-lg p-3 transition-all duration-200 border-gray-200 bg-gray-100 text-black"
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="h-4 w-4 rounded-full bg-red-500 flex items-center justify-center">
                    <Ban className="h-2 w-2 text-white" />
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <IconComponent className="h-4 w-4 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-gray-800">
                    {displayName} (Cancelled)
                  </span>
                </div>
              </div>
            </div>
          );
        }

        switch (invocation.state) {
          case "partial-call":
          case "call":
            return (
              <div
                key={index}
                className="border rounded-lg p-3 transition-all duration-200 border-gray-200 bg-gray-100 text-black"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  </div>
                  <div className="flex-shrink-0">
                    <IconComponent className="h-4 w-4 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-gray-800">
                      {displayName}
                    </span>
                  </div>
                </div>
              </div>
            );

          case "result":
            return (
              <div
                key={index}
                className="border rounded-lg p-3 transition-all duration-200 border-gray-200 bg-gray-100 text-black"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <div className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="h-2 w-2 text-white" />
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <IconComponent className="h-4 w-4 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-800">
                        {displayName}
                      </span>
                    </div>
                    {/* Show details if available
                    {invocation.result?.details && (
                      <div className="mt-1 text-xs text-gray-600">
                        {invocation.result.details}
                      </div>
                    )} */}
                  </div>
                </div>
              </div>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}
